import React, { useState, useEffect, useRef } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

// --- Types ---
type OutlineStep = {
  title: string;
  description: string;
};
type LevelSchedule = {
  level: string;
  startDate: string;
  registrationDeadline: string;
};
type CourseDetailsData = {
  title: string;
  image: string;
  description?: string;
  descriptionBn?: string;
  hasOutline?: boolean;
  outline?: OutlineStep[];
  outlineBn?: OutlineStep[];
  courseRegulations?: string;
  courseRegulationsBn?: string;
  requirements?: string[];
  requirementsBn?: string[];
  discontinued?: boolean;
  price?: number;
  discountedPrice?: number;
  priceBreakdown?: string[];
  discountedPriceBreakdown?: string[];
  enrollmentNote?: string;
  enrollmentNoteBn?: string;
  levelSchedules?: LevelSchedule[];
  startDate?: string | null;
  registrationDeadline?: string | null;
};

// --- Utility Functions ---
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function boldImportantWords(text: string, highlights: string[]) {
  if (!text) return "";
  let html = text;
  highlights.forEach((word) => {
    if (!word) return;
    html = html.replace(
      new RegExp(`(${escapeRegExp(word)})`, "gi"),
      "<strong>$1</strong>",
    );
  });
  return html;
}
function splitRegulations(text?: string) {
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}
function formatDate(dateStr?: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB").replace(/\//g, ".");
}

const highlights = [
  "paid vocational training",
  "monthly salary",
  "Bangladeshi students",
  "step-by-step support",
  "Guideline2Germany",
  "German",
  "visa",
  "installments",
  "prepared",
  "non-refundable",
  "Refund & Withdrawal",
  "Refund and Withdrawal",
  "language course",
  "application",
  "interview",
  "support",
  "preparation",
  "enrollment",
  "course fee",
  "Payment Option",
  "First Installment",
  "Payment Policy",
  "Payment Deadlines",
  "Course Commitment",
  "Refund Policy",
  "Financial Commitment",
  "Language Requirement",
  "Important Note",
  "Course Switching",
  "refunded",
  "Batch Completion",
  "University Admission",
  "Visa Paperwork",
  "motivation letter",
  "financial proof",
  "health insurance",
  "Ausbildung",
  "step",
  "document",
  "contract",
  "employer",
  "training",
  "salary",
  "non-academic",
  "future",
  "courses",
  "online",
  "live",
  "classes",
  "quizzes",
  "exams",
  "communication",
  "skills",
  "German",
  "Language knowledge",
  "academic background",
  "SSC + HSC/Diploma",
  "HSC/Diploma",
  "below 35",
];

type Props = {
  course: CourseDetailsData;
};

const CourseDetails: React.FC<Props> = ({ course }) => {
  const { t, i18n } = useTranslation();
  const isBn = i18n.language === "bn";
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const toggleAccordion = (index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const activeDescription = (isBn && course.descriptionBn) ? course.descriptionBn : course.description;
  const activeRegulations = (isBn && course.courseRegulationsBn) ? course.courseRegulationsBn : course.courseRegulations;
  const activeOutline = (isBn && course.outlineBn) ? course.outlineBn : course.outline;
  const activeRequirements = (isBn && course.requirementsBn) ? course.requirementsBn : course.requirements;
  const activeEnrollmentNote = (isBn && course.enrollmentNoteBn) ? course.enrollmentNoteBn : course.enrollmentNote;

  const regulationPoints = splitRegulations(activeRegulations);

  const hasIncrementedRef = useRef(false);

  useEffect(() => {
    const incrementVisit = async () => {
      if (hasIncrementedRef.current) return;
      hasIncrementedRef.current = true;

      try {
        const API = process.env.REACT_APP_API_BASE_URL;

        await fetch(`${API}/api/visits/increment`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            courseTitle: course.title,
          }),
        });
      } catch (error) {
        console.error("Error incrementing visit:", error);
      }
    };

    if (course && course.title) {
      incrementVisit();
    }
  }, [course]);

  const hasLevelSchedules =
    Array.isArray(course.levelSchedules) && course.levelSchedules.length > 0;
  const hasSingleAnnouncement =
    !!course.startDate || !!course.registrationDeadline;
  const hasAnnouncement = hasLevelSchedules || hasSingleAnnouncement;

  return (
    <>
      <Breadcrumb title={course.title} titleClassName={course.discontinued ? "line-through" : undefined} />

      {course.discontinued && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 px-6 py-4 text-sm">
          <strong>Note:</strong> We are not currently offering this course. Please check back later or contact us for more information.
        </div>
      )}

      <section className="bg-gray-50 py-10">
        <div className="container">
          <div className="row">
            {/* Left content */}
            <div className="col-lg-8">
              <ImageWithBasePath
                src={course.image}
                alt={course.title}
                className="img-fluid rounded mb-4"
              />

              <div className="bg-white p-5 shadow rounded">
                <h2 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-2xl font-semibold">
                  {t("courseDetail.overview")}
                </h2>
                <p
                  className="text-justify text-sm"
                  dangerouslySetInnerHTML={{
                    __html: boldImportantWords(
                      activeDescription || "",
                      highlights,
                    ),
                  }}
                />
              </div>

              {/* Requirements Section */}
              {activeRequirements && activeRequirements.length > 0 && (
                <div className="bg-white p-5 shadow rounded mt-6">
                  <h2 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-2xl font-semibold">
                    {t("courseDetail.requirements")}
                  </h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 text-sm">
                    {activeRequirements.map((req, idx) => (
                      <li
                        key={idx}
                        className="leading-relaxed text-justify"
                        dangerouslySetInnerHTML={{
                          __html: boldImportantWords(req, highlights),
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {course.hasOutline &&
                activeOutline &&
                activeOutline.length > 0 && (
                  <div className="bg-white p-5 shadow rounded mt-6">
                    <h3 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-xl font-semibold">
                      {t("courseDetail.outline")}
                    </h3>
                    <div className="space-y-2">
                      {activeOutline.map((step, index) => (
                        <div
                          key={index}
                          className="border rounded-lg overflow-hidden transition-all"
                        >
                          <button
                            className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                            onClick={() => toggleAccordion(index)}
                          >
                            <span className="font-medium text-gray-800 text-sm">
                              {step.title}
                            </span>
                            <FontAwesomeIcon
                              icon={
                                activeIndex === index
                                  ? faChevronUp
                                  : faChevronDown
                              }
                              className="w-5 h-5 text-gray-600"
                            />
                          </button>
                          <div
                            className={`transition-all duration-300 overflow-hidden ${
                              activeIndex === index
                                ? "max-h-[500px]"
                                : "max-h-0"
                            }`}
                          >
                            <div className="p-4 pt-0 text-gray-600 text-sm">
                              <p
                                className="text-justify"
                                dangerouslySetInnerHTML={{
                                  __html: boldImportantWords(
                                    step.description,
                                    highlights,
                                  ),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* Right panel */}
            <div className="col-lg-4">
              <div className="sticky top-20">
                <div className="bg-white p-5 shadow rounded">
                  {/* Course Announcement Section */}
                  {hasAnnouncement && (
                    <>
                      <h2 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-xl font-semibold">
                        {t("courseDetail.announcement")}
                      </h2>
                      {hasLevelSchedules && course.levelSchedules ? (
                        <table className="mb-6 w-full text-sm text-gray-800">
                          <thead>
                            <tr>
                              <th className="text-left font-semibold">{t("courseDetail.level")}</th>
                              <th className="text-left font-semibold">{t("courseDetail.batchDate")}</th>
                              <th className="text-left font-semibold">{t("courseDetail.regDeadline")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {course.levelSchedules.map((sched, idx) => (
                              <tr key={idx}>
                                <td className="pr-4">{sched.level}</td>
                                <td className="pr-4">
                                  {formatDate(sched.startDate)}
                                </td>
                                <td>
                                  {formatDate(sched.registrationDeadline)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <ul className="mb-6 text-sm text-gray-800">
                          {course.startDate && (
                            <li>
                              <span className="font-medium">
                                {t("courseDetail.newBatchDate")}:
                              </span>{" "}
                              {formatDate(course.startDate)}
                            </li>
                          )}
                          {course.registrationDeadline && (
                            <li>
                              <span className="font-medium">
                                {t("courseDetail.regDeadlineLabel")}:
                              </span>{" "}
                              {formatDate(course.registrationDeadline)}
                            </li>
                          )}
                        </ul>
                      )}
                      <hr className="my-4 border-gray-200" />
                    </>
                  )}

                  {/* Price Section Header */}
                  {!hasAnnouncement && (
                    <h4 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-lg font-semibold">
                      {t("courseDetail.priceDetails")}
                    </h4>
                  )}

                  {/* Single price display */}
                  {typeof course.price === "number" && (
                    <div className="mb-4">
                      {/* Old price — only show if there's a discount */}
                      {course.discountedPrice && (
                        <p className="text-gray-400 line-through text-sm mb-0.5">
                          {course.price.toLocaleString()} BDT
                        </p>
                      )}
                      {/* New price + badge */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-[#009fe3] text-2xl font-bold mb-0">
                          {(
                            course.discountedPrice ?? course.price
                          ).toLocaleString()}{" "}
                          BDT
                        </h4>
                        {course.discountedPrice && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                            30% OFF
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Multi-level price breakdown (German Language) */}
                  {!course.price &&
                    (course.priceBreakdown ||
                      course.discountedPriceBreakdown) && (
                      <div className="mb-4">
                        {/* Badge on top */}
                        {course.discountedPriceBreakdown && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full mb-3 inline-block">
                            30% OFF
                          </span>
                        )}
                        {course.discountedPriceBreakdown ? (
                          /* Show old → new for each level */
                          <table className="w-full text-sm">
                            <thead>
                              <tr>
                                <th className="text-left text-gray-400 font-semibold pb-2 text-xs border-b border-gray-100">
                                  {t("courseDetail.level")}
                                </th>
                                <th className="text-left text-gray-400 font-semibold pb-2 text-xs border-b border-gray-100">
                                  {t("courseDetail.before")}
                                </th>
                                <th className="text-left text-gray-400 font-semibold pb-2 text-xs border-b border-gray-100">
                                  {t("courseDetail.now")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {course.discountedPriceBreakdown.map(
                                (newPrice, idx) => {
                                  const oldEntry =
                                    course.priceBreakdown?.[idx] ?? "";
                                  const levelMatch =
                                    oldEntry.match(/^([A-Z0-9]+)\s+Level/);
                                  const levelLabel = levelMatch
                                    ? levelMatch[1]
                                    : `L${idx + 1}`;
                                  const oldAmount = oldEntry.replace(
                                    /^[A-Z0-9]+ Level:\s*/,
                                    "",
                                  );
                                  const newAmount = newPrice.replace(
                                    /^[A-Z0-9]+ Level:\s*/,
                                    "",
                                  );
                                  return (
                                    <tr key={idx}>
                                      <td className="py-1.5 font-bold text-gray-700">
                                        {levelLabel}
                                      </td>
                                      <td className="py-1.5 text-gray-400 line-through">
                                        {oldAmount}
                                      </td>
                                      <td className="py-1.5 font-bold text-[#009fe3]">
                                        {newAmount}
                                      </td>
                                    </tr>
                                  );
                                },
                              )}
                            </tbody>
                          </table>
                        ) : (
                          /* No discount — plain list */
                          <ul className="text-sm text-gray-700">
                            {course.priceBreakdown?.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                  {course.enrollmentNote && (
                    <p
                      className="text-sm text-gray-700 mb-4 text-justify px-4 py-2 bg-[#e0f7ff] rounded-md"
                      dangerouslySetInnerHTML={{
                        __html: boldImportantWords(
                          activeEnrollmentNote || "",
                          highlights,
                        ),
                      }}
                    />
                  )}

                  <Link
                    to={all_routes.courseCheckout}
                    state={{ course }}
                    className="btn bg-[#009fe3] w-full mt-3 text-white py-3 rounded-lg text-center hover:bg-[#007cb5]"
                  >
                    {t("courseDetail.enrollNow")}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Regulations Section */}
          {regulationPoints.length > 0 && (
            <div className="bg-white p-5 shadow rounded mt-6">
              <h2 className="bg-[#009fe3] text-white px-4 py-2 rounded mb-4 text-2xl font-semibold">
                {t("courseDetail.regulations")}
              </h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-sm">
                {regulationPoints.map((point, idx) => (
                  <li
                    key={idx}
                    className="leading-relaxed text-justify"
                    dangerouslySetInnerHTML={{
                      __html: boldImportantWords(point, highlights),
                    }}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CourseDetails;
